import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Backdrop, Box, CircularProgress } from '@mui/material';
import { PageIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { CalendarWidget, CalendarFetchParams } from '@components/calendar/CalendarWidget';
import { PmCalendarEventModal } from './PmCalendarEventModal';
import { toastService } from '@services/toast.service.ts';
import { sleepFactsInRange } from '@/services/sleep.service';
import {
  getPmClientCalendarEvent,
  getPmClientCalendarEvents,
  getPmClientCalendarOptions,
  getPmClientSleepSessions,
} from '@services/pm-client-calendar.service.ts';
import { Calendar as AppCalendar, CalendarEventDto, CalendarEventType } from '@/types/calendar';

// Календар клієнта для продакт-менеджера (2026-10-10). Окремий клон експертського ClientCalendarTab на тих самих
// компонентах (CalendarWidget), але лише перегляд: без додавання, редагування, видалення й
// перетягування подій - ці обробники віджету просто не передаються. Деталі події - одне власне вікно продакта
// (PmCalendarEventModal) для всіх типів. Дані - з окремих маршрутів `pm/clients/:id/...`.
export const PmClientCalendarTab: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [calendars, setCalendars] = useState<AppCalendar[]>([]);
  const [eventTypes, setEventTypes] = useState<CalendarEventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEvent, setDetailEvent] = useState<CalendarEventDto | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getPmClientCalendarOptions(clientId)
      .then((options) => {
        setCalendars(options.calendars);
        setEventTypes(options.eventTypes);
      })
      .catch(() => setError('Не вдалося завантажити параметри календаря'))
      .finally(() => setLoading(false));
  }, [clientId]);

  const fetchEvents = useCallback(
    ({ calendarIds, eventTypeIds, dateFrom, dateTo }: CalendarFetchParams) =>
      getPmClientCalendarEvents(clientId, {
        calendarId: calendarIds.length === calendars.length ? undefined : calendarIds.join(','),
        eventTypeIds: eventTypeIds.join(','),
        dateFrom,
        dateTo,
        limit: 500,
      }),
    [clientId, calendars.length],
  );

  const fetchSleepFacts = useCallback(
    async (from: Date, to: Date) => sleepFactsInRange(await getPmClientSleepSessions(clientId, from, to), from, to),
    [clientId],
  );

  const openEventDetail = async (eventId: number) => {
    setDetailLoading(true);
    try {
      setDetailEvent(await getPmClientCalendarEvent(clientId, eventId));
    } catch {
      toastService.error('Не вдалося завантажити деталі події');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-i-doc={PageIndices.PM_CLIENT_CALENDAR}>
      <PageHint iDoc={PageIndices.PM_CLIENT_CALENDAR} />

      <Backdrop sx={{ color: 'common.white', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={detailLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
      ) : (
        <CalendarWidget
          calendars={calendars}
          eventTypes={eventTypes}
          fetchEvents={fetchEvents}
          fetchSleepFacts={fetchSleepFacts}
          storagePrefix={`pm-client-calendar-${clientId}`}
          persistDate
          onEventClick={openEventDetail}
        />
      )}

      <PmCalendarEventModal open={!!detailEvent} clientId={clientId} event={detailEvent} onClose={() => setDetailEvent(null)} />
    </Box>
  );
};
